import React, { useState, useEffect, useCallback } from 'react';
import {
  CircularProgress, Alert, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, FormControl, InputLabel, Select, MenuItem,
  IconButton, Typography, Box, Snackbar
} from '@mui/material';
import { FormControlLabel, Switch } from '@mui/material';
import {
  ArrowBack, Edit, Add, Close, Agriculture, Restaurant,
  Favorite, BugReport, Waves, Cloud,
  Science, PlayArrow, WaterDrop, Thermostat, Opacity,
  Biotech, Sensors, Notifications, Warning
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import {
  pondsAPI, farmsAPI, stockingEventsAPI, alertsAPI
} from 'services/api';
import DOCProgressIndicator from 'shared/components/DOCProgressIndicator';
import WeatherCard from 'shared/components/WeatherCard';
import PondTimeline from 'shared/components/PondTimeline';
import InlineFeedEntry from 'shared/components/InlineFeedEntry';
import FeedManagement from 'shared/components/FeedManagement';
import { getMaxStockingDensity, validateStockingDensity } from 'shared/utils/caaLimits';

// ============================================================================
// SPECIES COLORS
// ============================================================================

const SPECIES_COLORS = {
  vannamei: { bg: '#E3F2FD', text: '#1565C0' },
  monodon: { bg: '#E8F5E9', text: '#2E7D32' },
  scampi:  { bg: '#FFF3E0', text: '#E65100' },
  indicus: { bg: '#F3E5F5', text: '#7B1FA2' },
};

// ============================================================================
// HEALTH STATUS (Placeholder — IoT/AI ready)
// ============================================================================

const getHealthStatus = (pond, stockingEvent) => {
  const active = stockingEvent && pond?.status === 'in_crop';
  return {
    overall:       active ? 'Good' : 'Unknown',
    overallColor:  active ? '#4CAF50' : '#9E9E9E',
    disease:       'Low',
    diseaseColor:  '#4CAF50',
    waterRisk:     'Stable',
    waterRiskColor:'#2196F3',
    weatherRisk:   'Normal',
    weatherRiskColor: '#4CAF50',
  };
};

// ============================================================================
// REUSABLE SUB-COMPONENTS
// ============================================================================

const HealthCard = ({ title, value, icon, color, subtitle, placeholder = true }) => (
  <div
    className="bg-white rounded-2xl shadow-sm p-4 transition-all duration-200 hover:shadow-md flex flex-col min-h-[110px]"
    style={{ borderLeft: `3px solid ${color}` }}
  >
    <div className="flex items-start justify-between flex-1">
      <div>
        <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">{title}</p>
        <p className="text-lg font-bold mt-1" style={{ color }}>{value}</p>
        {subtitle && <p className="text-[11px] text-gray-400 mt-0.5">{subtitle}</p>}
      </div>
      <div
        className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
        style={{ backgroundColor: `${color}15`, color }}
      >
        {icon}
      </div>
    </div>
    {placeholder && (
      <span className="inline-flex self-start mt-auto pt-2 px-2 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-400">
        IoT Ready
      </span>
    )}
  </div>
);

const SensorPlaceholderCard = () => {
  const params = [
    { label: 'Dissolved O₂', icon: <Opacity sx={{ fontSize: 14 }} />, unit: 'mg/L', ideal: '> 4 mg/L' },
    { label: 'Temperature',  icon: <Thermostat sx={{ fontSize: 14 }} />, unit: '°C', ideal: '28-32°C' },
    { label: 'pH Level',     icon: <Science sx={{ fontSize: 14 }} />, unit: '', ideal: '7.5-8.5' },
    { label: 'Salinity',     icon: <WaterDrop sx={{ fontSize: 14 }} />, unit: 'ppt', ideal: '15-25 ppt' },
    { label: 'Ammonia',      icon: <Biotech sx={{ fontSize: 14 }} />, unit: 'mg/L', ideal: '< 0.1 mg/L' },
  ];

  return (
    <div className="bg-white rounded-2xl shadow-sm p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Sensors sx={{ fontSize: 16, color: '#9E9E9E' }} />
          <span className="text-sm font-semibold text-gray-700">Sensors</span>
        </div>
        <span className="text-[10px] font-medium bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
          Offline
        </span>
      </div>
      <div className="space-y-2">
        {params.map((p, i) => (
          <div key={i} className="flex items-center justify-between bg-gray-50 rounded-xl px-3 py-2.5">
            <div className="flex items-center gap-2">
              <span className="text-gray-400">{p.icon}</span>
              <span className="text-xs font-medium text-gray-600">{p.label}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-bold text-gray-300">—</span>
              {p.unit && <span className="text-[10px] text-gray-400">{p.unit}</span>}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 bg-gray-50 rounded-xl border border-dashed border-gray-300 p-3 flex items-start gap-2">
        <Sensors sx={{ fontSize: 14, color: '#bbb', mt: 0.25 }} />
        <div>
          <p className="text-[11px] text-gray-500 font-medium">Waiting for sensor data</p>
          <p className="text-[10px] text-gray-400 mt-0.5">Auto-enabled once IoT devices are connected.</p>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN SINGLE POND DASHBOARD
// ============================================================================

const SinglePondDashboard = () => {
  const { pondId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pond, setPond] = useState(null);
  const [farm, setFarm] = useState(null);
  const [stockingEvent, setStockingEvent] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // Feed management dialog
  const [openFeedDialog, setOpenFeedDialog] = useState(false);

  // Stocking event dialog
  const [openStockingDialog, setOpenStockingDialog] = useState(false);
  const [editingStockingEvent, setEditingStockingEvent] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [stockingFormData, setStockingFormData] = useState({
    stocking_date: '', species: 'Vannamei', stocking_density_per_m2: '',
    spf_seed: false, hatchery_name: '', seed_quantity: '', seed_size: '',
  });

  // ---------- DATA LOADING ----------

  const loadPondData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const pondRes = await pondsAPI.getById(parseInt(pondId));
      const pondData = pondRes.data;
      setPond(pondData);

      if (pondData.farm_id) {
        try {
          const farmRes = await farmsAPI.getById(pondData.farm_id);
          setFarm(farmRes.data);
        } catch (e) { console.warn('Failed to load farm:', e); }
      }

      try {
        const eventsRes = await stockingEventsAPI.getByPond(parseInt(pondId));
        const events = eventsRes.data || [];
        const sorted = [...events].sort((a, b) => new Date(b.stocking_date) - new Date(a.stocking_date));
        setStockingEvent(sorted[0] || null);
      } catch (e) { console.warn('Stocking events error:', e); }

      try {
        const alertsRes = await alertsAPI.getAll({ pondId: parseInt(pondId), limit: 10 });
        setAlerts(alertsRes.data || []);
      } catch (e) { console.warn('Alerts error:', e); }

    } catch (err) {
      console.error('Error loading pond data:', err);
      setError('Failed to load pond data. The pond may not exist or you may not have access.');
    } finally {
      setLoading(false);
    }
  }, [pondId]);

  useEffect(() => { loadPondData(); }, [loadPondData]);

  // ---------- STOCKING EVENT HANDLERS ----------

  const handleOpenStockingDialog = (event = null) => {
    if (event) {
      setEditingStockingEvent(event);
      setStockingFormData({
        stocking_date: event.stocking_date ? event.stocking_date.split('T')[0] : '',
        species: event.species || 'Vannamei',
        stocking_density_per_m2: event.stocking_density_per_m2 || event.stocking_density || '',
        spf_seed: event.spf_seed || false,
        hatchery_name: event.hatchery_name || '',
        seed_quantity: event.seed_quantity || '',
        seed_size: event.seed_size || '',
      });
    } else {
      setEditingStockingEvent(null);
      const today = new Date().toISOString().split('T')[0];
      setStockingFormData({
        stocking_date: today, species: pond?.species || 'Vannamei',
        stocking_density_per_m2: '', spf_seed: false, hatchery_name: '',
        seed_quantity: '', seed_size: '',
      });
    }
    setOpenStockingDialog(true);
  };

  const handleCloseStockingDialog = () => {
    setOpenStockingDialog(false);
    setEditingStockingEvent(null);
    setFieldErrors({});
  };

  const handleSubmitStockingEvent = async () => {
    if (!stockingFormData.stocking_date || !stockingFormData.stocking_density_per_m2) {
      setSnackbar({ open: true, message: 'Please fill required fields', severity: 'error' });
      return;
    }
    const density = parseFloat(stockingFormData.stocking_density_per_m2);
    const validation = validateStockingDensity(stockingFormData.species, density);
    if (!validation.isValid) {
      setFieldErrors({ stocking_density_per_m2: validation.errorMessage });
      setSnackbar({ open: true, message: validation.errorMessage, severity: 'error' });
      return;
    }
    try {
      const submitData = {
        pond_id: parseInt(pondId),
        stocking_date: stockingFormData.stocking_date,
        species: stockingFormData.species,
        stocking_density_per_m2: density,
        spf_seed: stockingFormData.spf_seed,
        hatchery_name: stockingFormData.hatchery_name || null,
        seed_quantity: stockingFormData.seed_quantity ? parseInt(stockingFormData.seed_quantity) : null,
        seed_size: stockingFormData.seed_size || null,
      };
      if (editingStockingEvent) {
        await stockingEventsAPI.update(editingStockingEvent.id, submitData);
        setSnackbar({ open: true, message: 'Stocking event updated', severity: 'success' });
      } else {
        await stockingEventsAPI.create(submitData);
        setSnackbar({ open: true, message: 'Stocking event created', severity: 'success' });
      }
      handleCloseStockingDialog();
      loadPondData();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.detail || 'Failed to save', severity: 'error' });
    }
  };

  // ---------- HELPERS ----------

  const getSpeciesDisplayName = (species) => {
    const map = { vannamei: 'Vannamei', monodon: 'Monodon', scampi: 'Scampi', indicus: 'Indicus' };
    return map[species?.toLowerCase()] || species || 'Unknown';
  };

  const getStatusLabel = (status) => {
    const map = { active: 'Ready', in_crop: 'Active Crop', crop_out: 'Harvested', maintenance: 'Maintenance', inactive: 'Inactive' };
    return map[status?.toLowerCase()] || status || 'Unknown';
  };

  const getStatusColor = (status) => {
    const map = { active: '#4CAF50', in_crop: '#1976D2', crop_out: '#00BCD4', maintenance: '#FF9800', inactive: '#F44336' };
    return map[status?.toLowerCase()] || '#9E9E9E';
  };

  const getCropStage = () => {
    if (!stockingEvent || !stockingEvent.stocking_date) return { stage: 'Not Stocked', day: 0 };
    const diff = Math.floor((Date.now() - new Date(stockingEvent.stocking_date).getTime()) / 86400000);
    if (diff < 0) return { stage: 'Pre-Stocking', day: 0 };
    if (diff <= 30) return { stage: 'Nursery', day: diff, color: '#00BCD4' };
    if (diff <= 60) return { stage: 'Grow-Out Early', day: diff, color: '#2196F3' };
    if (diff <= 90) return { stage: 'Grow-Out Mid', day: diff, color: '#FF9800' };
    if (diff <= 120) return { stage: 'Grow-Out Late', day: diff, color: '#F44336' };
    return { stage: 'Harvest Ready', day: diff, color: '#4CAF50' };
  };

  // ---------- LOADING / ERROR ----------

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <CircularProgress />
      </div>
    );
  }

  if (error || !pond) {
    return (
      <div className="px-6 py-6">
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error || 'Pond not found.'}</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/farmer/ponds')}>Back to Ponds</Button>
      </div>
    );
  }

  const healthStatus = getHealthStatus(pond, stockingEvent);
  const statusColor = getStatusColor(pond.status);
  const speciesKey = pond.species?.toLowerCase() || 'vannamei';
  const speciesColor = SPECIES_COLORS[speciesKey] || { bg: '#F5F5F5', text: '#616161' };
  const unreadAlerts = alerts.filter(a => !a.acknowledged).length;
  const cropStage = getCropStage();

  // ---------- RENDER ----------

  return (
    <div className="px-6 py-6 space-y-6 max-w-screen-2xl mx-auto page-transition-enter">

      {/* ================================================================
          ROW 1 — POND IDENTITY HEADER
          ================================================================ */}
      <div className="bg-white rounded-2xl shadow-sm p-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          {/* Left: Back + Identity */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/farmer/ponds')}
              className="w-10 h-10 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition shrink-0"
            >
              <ArrowBack sx={{ fontSize: 18, color: '#666' }} />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900 leading-tight">
                {pond.pond_name}
              </h1>
              <p className="text-sm text-gray-500 mt-0.5">
                {farm?.name || 'Unknown Farm'}
              </p>
            </div>
          </div>

          {/* Right: Badges + Edit */}
          <div className="flex items-center gap-2 flex-wrap">
            {/* Status badge */}
            <span
              className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold"
              style={{
                backgroundColor: `${statusColor}14`,
                color: statusColor,
                border: `1px solid ${statusColor}30`
              }}
            >
              {getStatusLabel(pond.status)}
            </span>
            {/* Species badge */}
            <span
              className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold"
              style={{ backgroundColor: speciesColor.bg, color: speciesColor.text }}
            >
              🦐 {getSpeciesDisplayName(pond.species)}
            </span>
            {/* Edit button — opens edit modal on the overview page */}
            <Button
              variant="outlined"
              size="small"
              startIcon={<Edit />}
              onClick={() => navigate(`/farmer/ponds?action=edit&pondId=${pond.id}`)}
              sx={{ textTransform: 'none', borderRadius: 2, fontSize: '0.8rem' }}
            >
              Edit Pond
            </Button>
          </div>
        </div>
      </div>

      {/* ================================================================
          ROW 2 — HEALTH SNAPSHOT GRID
          ================================================================ */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <HealthCard
          title="Overall Status"
          value={healthStatus.overall}
          icon={<Favorite sx={{ fontSize: 18 }} />}
          color={healthStatus.overallColor}
          subtitle="Based on all metrics"
        />
        <HealthCard
          title="Disease Risk"
          value={healthStatus.disease}
          icon={<BugReport sx={{ fontSize: 18 }} />}
          color={healthStatus.diseaseColor}
          subtitle="AI prediction"
        />
        <HealthCard
          title="Water Risk"
          value={healthStatus.waterRisk}
          icon={<Waves sx={{ fontSize: 18 }} />}
          color={healthStatus.waterRiskColor}
          subtitle="Last 24 hours"
        />
        <HealthCard
          title="Weather Risk"
          value={healthStatus.weatherRisk}
          icon={<Cloud sx={{ fontSize: 18 }} />}
          color={healthStatus.weatherRiskColor}
          subtitle="Next 48 hours"
        />
        <HealthCard
          title="Alerts"
          value={unreadAlerts > 0 ? `${unreadAlerts} Active` : 'All Clear'}
          icon={<Notifications sx={{ fontSize: 18 }} />}
          color={unreadAlerts > 0 ? '#FF9800' : '#4CAF50'}
          subtitle={unreadAlerts > 0 ? 'Review needed' : 'No pending alerts'}
          placeholder={false}
        />
      </div>

      {/* ================================================================
          ROW 3 — MAIN CONTENT (12-col grid)
          ================================================================ */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

        {/* LEFT COLUMN — lg:col-span-8 */}
        <div className="lg:col-span-8 space-y-6">

          {/* Feed Status Card */}
          <div className="bg-white rounded-2xl shadow-sm p-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center">
                <Restaurant sx={{ fontSize: 16, color: '#4CAF50' }} />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-gray-800">Feed Status</h3>
                <p className="text-[11px] text-gray-400">Log and track feeding activity</p>
              </div>
              <Button
                variant="outlined"
                size="small"
                color="success"
                startIcon={<Restaurant sx={{ fontSize: 14 }} />}
                onClick={() => setOpenFeedDialog(true)}
                sx={{ textTransform: 'none', fontSize: '0.75rem', borderRadius: 2 }}
              >
                Feed Mgmt
              </Button>
            </div>
            <InlineFeedEntry
              pondId={pond.id}
              pondName={pond.pond_name}
              onFeedAdded={() => {}}
              compact={false}
            />
          </div>

          {/* DOC Progress Card */}
          <div className="bg-white rounded-2xl shadow-sm p-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                <PlayArrow sx={{ fontSize: 16, color: '#1976D2' }} />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-800">DOC Progress</h3>
                <p className="text-[11px] text-gray-400">Days of Culture tracking</p>
              </div>
            </div>
            {stockingEvent && stockingEvent.stocking_date ? (
              <div className="bg-blue-50/50 rounded-xl p-4 border border-blue-100">
                <DOCProgressIndicator
                  stockingDate={stockingEvent.stocking_date}
                  targetDOC={pond.doc_requested || 120}
                  variant="circular"
                  size="small"
                  showDetails={true}
                  label="Days of Culture"
                />
              </div>
            ) : (
              <div className="bg-gray-50 rounded-xl p-6 text-center border border-dashed border-gray-300">
                <p className="text-sm text-gray-500 font-medium">No active stocking event</p>
                <p className="text-xs text-gray-400 mt-1 mb-3">Create a stocking event to start DOC tracking</p>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Add />}
                  onClick={() => handleOpenStockingDialog()}
                  sx={{ textTransform: 'none', fontSize: '0.8rem', borderRadius: 2 }}
                >
                  Add Stocking Event
                </Button>
              </div>
            )}
          </div>

          {/* Crop Stage Card */}
          <div className="bg-white rounded-2xl shadow-sm p-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center">
                <Agriculture sx={{ fontSize: 16, color: '#9C27B0' }} />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-800">Crop Stage</h3>
                <p className="text-[11px] text-gray-400">Current culture cycle details</p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-gray-50 rounded-xl p-3">
                <p className="text-[10px] uppercase tracking-wide text-gray-400 font-medium">Stage</p>
                <p className="text-sm font-bold mt-0.5" style={{ color: cropStage.color || '#9E9E9E' }}>
                  {cropStage.stage}
                </p>
              </div>
              <div className="bg-gray-50 rounded-xl p-3">
                <p className="text-[10px] uppercase tracking-wide text-gray-400 font-medium">DOC Day</p>
                <p className="text-sm font-bold text-gray-800 mt-0.5">
                  {cropStage.day > 0 ? `Day ${cropStage.day}` : '—'}
                </p>
              </div>
              <div className="bg-gray-50 rounded-xl p-3">
                <p className="text-[10px] uppercase tracking-wide text-gray-400 font-medium">Area</p>
                <p className="text-sm font-bold text-gray-800 mt-0.5">
                  {pond.pond_area ? `${parseFloat(pond.pond_area).toFixed(2)} ha` : '—'}
                </p>
              </div>
              <div className="bg-gray-50 rounded-xl p-3">
                <p className="text-[10px] uppercase tracking-wide text-gray-400 font-medium">Density</p>
                <p className="text-sm font-bold text-gray-800 mt-0.5">
                  {pond.stocking_density ? `${parseFloat(pond.stocking_density).toFixed(1)}/m²` : '—'}
                </p>
              </div>
            </div>
            {stockingEvent && (
              <div className="mt-3 flex items-center gap-2 text-xs text-gray-400">
                <span>Stocked: {new Date(stockingEvent.stocking_date).toLocaleDateString()}</span>
                <span>•</span>
                <span>Target: {pond.doc_requested || 120} days</span>
                {stockingEvent.hatchery_name && (
                  <>
                    <span>•</span>
                    <span>Hatchery: {stockingEvent.hatchery_name}</span>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT COLUMN — lg:col-span-4, sticky */}
        <div className="lg:col-span-4">
          <div className="lg:sticky lg:top-6 space-y-6">

            {/* Weather Card */}
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <WeatherCard
                farmId={pond.farm_id}
                compact={true}
              />
            </div>

            {/* Sensor Placeholder Card */}
            <SensorPlaceholderCard />

          </div>
        </div>
      </div>

      {/* ================================================================
          ROW 4 — TIMELINE (Full Width)
          ================================================================ */}
      <div className="bg-white rounded-2xl shadow-sm p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
              <Warning sx={{ fontSize: 16, color: '#1976D2' }} />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-800">Activity Timeline</h3>
              <p className="text-[11px] text-gray-400">Recent pond events and activities</p>
            </div>
          </div>
        </div>
        <PondTimeline pondId={pond.id} compact={false} maxHeight={500} />
      </div>

      {/* ================================================================
          DIALOGS
          ================================================================ */}

      {/* Feed Management Dialog */}
      <Dialog
        open={openFeedDialog}
        onClose={() => setOpenFeedDialog(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { minHeight: '70vh', borderRadius: 3 } }}
      >
        <DialogTitle sx={{ bgcolor: 'success.main', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Restaurant />
            Feed Management - {pond.pond_name}
          </Box>
          <IconButton onClick={() => setOpenFeedDialog(false)} sx={{ color: 'white' }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2, bgcolor: '#fafafa' }}>
          <FeedManagement pondId={pond.id} pondName={pond.pond_name} onClose={() => setOpenFeedDialog(false)} />
        </DialogContent>
      </Dialog>

      {/* Stocking Event Dialog */}
      <Dialog
        open={openStockingDialog}
        onClose={handleCloseStockingDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, maxHeight: '90vh' } }}
      >
        <DialogTitle sx={{ background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Agriculture sx={{ fontSize: 24 }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                {editingStockingEvent ? 'Edit Stocking Event' : 'Add New Stocking Event'}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.75rem' }}>
                Pond: {pond.pond_name}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleCloseStockingDialog} size="small" sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)' }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2.5, bgcolor: '#fafafa' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField fullWidth type="date" label="Stocking Date *" value={stockingFormData.stocking_date} onChange={(e) => setStockingFormData({ ...stockingFormData, stocking_date: e.target.value })} InputLabelProps={{ shrink: true }} required />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth required>
                <InputLabel>Species *</InputLabel>
                <Select value={stockingFormData.species} label="Species *" onChange={(e) => setStockingFormData({ ...stockingFormData, species: e.target.value })}>
                  <MenuItem value="Vannamei">Vannamei</MenuItem>
                  <MenuItem value="Monodon">Monodon</MenuItem>
                  <MenuItem value="Indicus">Indicus</MenuItem>
                  <MenuItem value="Scampi">Scampi</MenuItem>
                </Select>
              </FormControl>
              <TextField fullWidth type="number" label="Stocking Density (per m²) *" value={stockingFormData.stocking_density_per_m2} onChange={(e) => setStockingFormData({ ...stockingFormData, stocking_density_per_m2: e.target.value })} required error={!!fieldErrors.stocking_density_per_m2} helperText={fieldErrors.stocking_density_per_m2 || `CAA Max: ${getMaxStockingDensity(stockingFormData.species) || 'N/A'}/m²`} />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField fullWidth label="Hatchery Name" value={stockingFormData.hatchery_name} onChange={(e) => setStockingFormData({ ...stockingFormData, hatchery_name: e.target.value })} />
              <TextField fullWidth type="number" label="Seed Quantity" value={stockingFormData.seed_quantity} onChange={(e) => setStockingFormData({ ...stockingFormData, seed_quantity: e.target.value })} />
              <TextField fullWidth label="Seed Size" value={stockingFormData.seed_size} onChange={(e) => setStockingFormData({ ...stockingFormData, seed_size: e.target.value })} placeholder="PL10" />
            </Box>
            <FormControlLabel control={<Switch checked={stockingFormData.spf_seed} onChange={(e) => setStockingFormData({ ...stockingFormData, spf_seed: e.target.checked })} color="success" size="small" />} label={<Typography variant="body2" sx={{ fontWeight: 500 }}>SPF Seed (15% discount)</Typography>} />
            {stockingFormData.stocking_date && (
              <Box sx={{ bgcolor: '#e3f2fd', borderRadius: 1.5, p: 1.5, border: '1px solid #90caf9' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main', fontSize: '0.875rem' }}>DOC Preview</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <DOCProgressIndicator stockingDate={stockingFormData.stocking_date} targetDOC={pond.doc_requested || 120} variant="circular" size="small" showDetails={true} label="Days of Culture" />
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5, bgcolor: '#fafafa', borderTop: '1px solid #e0e0e0' }}>
          <Button onClick={handleCloseStockingDialog} variant="outlined">Cancel</Button>
          <Button onClick={handleSubmitStockingEvent} variant="contained" disabled={!stockingFormData.stocking_date || !stockingFormData.stocking_density_per_m2} startIcon={editingStockingEvent ? <Edit /> : <Add />}>
            {editingStockingEvent ? 'Update' : 'Create Event'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })} message={snackbar.message} />
    </div>
  );
};

export default SinglePondDashboard;
